import axios from 'axios'

import { parseAuthenticationHeader } from 'helpers/Functions'
import { isValidDate } from 'helpers/Functions/Date'
import { appendFilterValue, parseFilterBody } from 'helpers/Functions/Filters'
import { ILayoutItem } from 'types/GlobalDashboard'
import {
  TDashboardWidgetFullyLoaded,
  WidgetPatchBody,
  PeriodFilter,
  FunnelStage,
  TDashboardWidgetSettingsLoaded,
  TDashboardWidget,
  WidgetType
} from 'types/GlobalWidget'
import { getWithRedirect, postWithRedirect } from 'utils/axios'
import { FilterBody, Sort } from 'utils/types'
import { TPutFunnelStages } from './types'

import { TMetricSettings } from 'types/GlobalMetric'
import { TGlobalFilterApiInput } from 'types/GlobalGlobalFilter'

const BASE_URL =
  process.env.NODE_ENV === 'development'
    ? 'http://localhost:3990/'
    : process.env.REACT_APP_HOMEPAL_MDM_API_PROD

const URL = `${BASE_URL}v1/widgets`

type TPostDashboardWidgetBaseBody = {
  dashboard_id: string
  kpi_id: string | null
  width: number
  height: number
  x: number
  y: number
}

type TPostCopyDashboardWidgetBody = TPostDashboardWidgetBaseBody & {
  kpi_id: null
  widget_id: string
}

type TPostDashboardWidgetWithoutKpiBody = TPostDashboardWidgetBaseBody & {
  kpi_id: null
  type: WidgetType.TEXT | WidgetType.COMMENT | WidgetType.IMAGE
}

type TPostDashboardWidgetWithKpiBody = TPostDashboardWidgetBaseBody &
  TMetricSettings & {
    kpi_id: string
    title: string
  }

export type TPostDashboardWidgetBody =
  | TPostDashboardWidgetWithoutKpiBody
  | TPostDashboardWidgetWithKpiBody
  | TPostCopyDashboardWidgetBody

export interface QueryParams {
  filter?: FilterBody[]
  segment_by?: string | null
  sort?: Sort | null
  period_filter?: PeriodFilter
  from_date?: string
  to_date?: string
  companies?: string[] // api-access
}

export type PostQueryParams = {
  filter?: {
    [key: string]: {
      eq: string[] | string
    }
  }
  companies?: string[]
  entity_columns?: string[]
  offset?: string
  period_filter?: PeriodFilter
  sort?: string
  from_date?: string
  to_date?: string
  segment_by?: string
  global_filter?: TGlobalFilterApiInput
}

const getFromAndToManualDate = (from: string, to: string) => {
  const fromDate = new Date(from)
  const toDate = new Date(to)

  // Make sure it is same date
  fromDate.setHours(6)
  toDate.setHours(6)

  const parsedFromDate = fromDate.toLocaleDateString('sv')
  const parsedToDate = toDate.toLocaleDateString('sv')

  return { fromDate: parsedFromDate, toDate: parsedToDate }
}

const getManualPeriodQuery = (from: string, to: string) => {
  let specificDateQuery = ''

  const { fromDate, toDate } = getFromAndToManualDate(from, to)

  specificDateQuery = `from_date=${fromDate}&to_date=${toDate}`

  return specificDateQuery
}

export const parseQueryParam = (queryParam: QueryParams, rawQuery = '') => {
  let queryString = rawQuery

  if (queryParam.period_filter) {
    queryString = `period_filter=${queryParam.period_filter}`
  }

  if (queryParam.filter && queryParam.filter.length > 0) {
    if (queryString) {
      queryString += '&'
    }

    queryString += queryParam.filter
      .map((filter) => parseFilterBody(filter.attributeId, filter))
      .filter(Boolean)
      .join('&')
  }

  if (queryParam.segment_by) {
    if (queryString) {
      queryString += '&'
    }

    queryString += `segment_by=${encodeURIComponent(queryParam.segment_by)}`
  }

  if (queryParam.sort) {
    if (queryString) {
      queryString += '&'
    }

    queryString += `sort=${queryParam?.sort.direction === 'desc' ? '-' : ''}${
      queryParam?.sort.attribute
    }`
  }

  if (
    queryParam.from_date &&
    queryParam.to_date &&
    isValidDate(new Date(queryParam.from_date)) &&
    isValidDate(new Date(queryParam.to_date))
  ) {
    if (queryString) {
      queryString += '&'
    }

    queryString += getManualPeriodQuery(
      queryParam.from_date,
      queryParam.to_date
    )
  }

  if (queryParam.companies && queryParam.companies.length > 0) {
    if (queryString) {
      queryString += '&'
    }

    queryString += `companies=${queryParam.companies.join(',')}`
  }

  if (queryString && queryString[0] !== '?') {
    queryString = '?' + queryString
  }

  return queryString
}

export const parseQueryParamForPost = (
  queryParam: QueryParams | null,
  globalFilter: TGlobalFilterApiInput
) => {
  // Add global filter
  const postQueryParams: PostQueryParams = {}

  postQueryParams.global_filter = globalFilter

  if (!queryParam) {
    return postQueryParams
  }

  postQueryParams.period_filter = queryParam.period_filter

  const filter: {
    [key: string]: {
      eq: string[]
    }
  } = {}

  if (queryParam.filter && queryParam.filter.length > 0) {
    queryParam.filter.forEach((f) => {
      if (f.attributeId in filter) {
        filter[f.attributeId].eq = appendFilterValue(
          filter[f.attributeId].eq,
          f.condition,
          f.value
        )
      } else {
        filter[f.attributeId] = {
          eq: appendFilterValue([], f.condition, f.value)
        }
      }
    })
    postQueryParams.filter = filter
  }

  if (queryParam.segment_by) {
    postQueryParams.segment_by = queryParam.segment_by
  }

  if (queryParam.sort) {
    postQueryParams.sort = `${
      queryParam?.sort.direction === 'desc' ? '-' : ''
    }${queryParam?.sort.attribute}`
  }

  if (
    queryParam.from_date &&
    queryParam.to_date &&
    isValidDate(new Date(queryParam.from_date)) &&
    isValidDate(new Date(queryParam.to_date))
  ) {
    const { fromDate, toDate } = getFromAndToManualDate(
      queryParam.from_date,
      queryParam.to_date
    )

    postQueryParams.from_date = fromDate
    postQueryParams.to_date = toDate
  }

  if (queryParam.companies && queryParam.companies.length > 0) {
    postQueryParams.companies = queryParam.companies
  }

  return postQueryParams
}

export async function getOne(
  id: string,
  queryParam: QueryParams | null,
  globalFilter: TGlobalFilterApiInput
) {
  const url = `${URL}/${id}`

  return postWithRedirect<TDashboardWidgetFullyLoaded>(url, {
    data: parseQueryParamForPost(queryParam, globalFilter)
  })
}

export async function create(data: TPostDashboardWidgetBody) {
  return axios.post<{
    data: TDashboardWidgetSettingsLoaded
    available_space: ILayoutItem | null
  }>(URL, { data }, parseAuthenticationHeader())
}

export async function update(id: string, body: WidgetPatchBody) {
  return axios.patch<{ data: TDashboardWidgetSettingsLoaded }>(
    `${URL}/${id}`,
    body,
    parseAuthenticationHeader()
  )
}

export async function destroy(id: string) {
  return axios.delete<{ available_space: ILayoutItem | null }>(
    `${URL}/${id}`,
    parseAuthenticationHeader()
  )
}

// Get data from an embedded widget
export async function getOnePublic(
  id: string,
  queryParam: QueryParams | null = null,
  rawQueryParams = ''
) {
  const parsedQueryParams = queryParam
    ? parseQueryParam(queryParam, rawQueryParams)
    : rawQueryParams
  const url = `${BASE_URL}v1/public/widgets/${id}${parsedQueryParams}`

  return getWithRedirect<TDashboardWidgetFullyLoaded>(url, {})
}

// Get data from a widget with api access
export async function getOneApiAccess(
  id: string,
  token: string,
  queryParam: QueryParams | null = null,
  rawQueryParams = ''
) {
  const parsedQueryParams = queryParam
    ? parseQueryParam(queryParam, rawQueryParams)
    : rawQueryParams
  const url = `${BASE_URL}v1/api-access/widgets/${id}${parsedQueryParams}`

  return getWithRedirect<TDashboardWidgetFullyLoaded>(url, {
    headers: { Authorization: `Bearer ${token}` }
  })
}

export async function putImage(
  id: string,
  image: Blob,
  name: string,
  queryParam: QueryParams | null
) {
  const formData = new FormData()
  const parsedQueryParams = queryParam ? parseQueryParam(queryParam) : ''

  formData.append('image', image)
  formData.append('name', name)

  return axios.put<{ data: TDashboardWidget }>(
    `${URL}/${id}/image${parsedQueryParams}`,
    formData,
    parseAuthenticationHeader()
  )
}

export async function putFunnelStages(id: string, data: TPutFunnelStages) {
  return axios.put<{ data: FunnelStage[] }>(
    `${URL}/${id}/funnel-stages`,
    { data: { funnel_stages: data } },
    parseAuthenticationHeader()
  )
}

export function skipFetchDataOnUpdate(
  body: WidgetPatchBody,
  periodFilterIsSet = false
) {
  return (
    'title' in body.data ||
    'subtitle' in body.data ||
    'embeddable' in body.data ||
    'min_value' in body.data ||
    'max_value' in body.data ||
    'show_title' in body.data ||
    'show_date' in body.data ||
    'label_chars' in body.data ||
    'invert_y_axis' in body.data ||
    'top_n_limit' in body.data ||
    ('date_filter' in body.data && periodFilterIsSet) ||
    'breakdown_by_display_type' in body.data ||
    'show_header' in body.data ||
    'show_vertical_dividers' in body.data ||
    'expanded_segment_levels' in body.data ||
    'element_order' in body.data ||
    'segment_by_display_name' in body.data ||
    ('longitude' in body.data &&
      'latitude' in body.data &&
      'zoom' in body.data) ||
    'pitch' in body.data ||
    'show_subtitle' in body.data ||
    'subtitle' in body.data ||
    'key_figure_visualization_type' in body.data ||
    'pie_chart_value_visualization_type' in body.data ||
    'is_preview' in body.data ||
    'tooltip' in body.data ||
    'text_align' in body.data ||
    'text_vertical_align' in body.data
  )
}
